import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { get, ApiError } from 'aws-amplify/api';
import { useNProgressLoader } from '../hooks/useNProgressLoader';
import awsmobile from '../aws-exports';
import './CostsList.css';

Amplify.configure(awsmobile);

// Allocated AWS spend. Small figures matter here — a domain can legitimately
// cost cents — so only abbreviate once it runs past a thousand dollars.
// The sign goes outside the symbol: -$6.92, never $-6.92.
function formatMoney(n) {
    const v = Number(n) || 0;
    const sign = v < 0 ? '-' : '';
    const a = Math.abs(v);
    if (a >= 1000) return `${sign}$${(a / 1000).toFixed(1)}k`;
    if (a >= 1) return `${sign}$${a.toFixed(2)}`;
    return `${sign}$${a.toFixed(2)}`;
}

// Helper function to format large numbers
function formatNumber(num) {
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
        return `${(num / 1000).toFixed(2)}K`;
    }
    return num.toString();
}

// Shown on hover over the flagged row. Traced 2026-08-03: these events carry
// label.origin = "about:srcdoc" — banners rendered inside srcdoc iframes by ad
// exchanges, where the browser reports no page URL, so no domain can be derived.
// The referer is usually still present, so it is recoverable in the tracking tag.
const UNATTRIBUTED_HELP =
    'Real activity we could not tie to a domain. Mostly banners rendered inside ' +
    'srcdoc iframes by ad exchanges, where the browser reports no page address — ' +
    'so the tracker has no domain to record. It is a tracking gap, not a traffic ' +
    'gap: the totals are correct, only the attribution is missing. The tracking ' +
    'tag was fixed in September 2026; from then on this row is near zero.';

// The tracking tag could not record a page domain for banner traffic served in
// srcdoc iframes, so those events show as Unattributed and every real domain is
// undercounted by their share. The tag was fixed upstream: September 2026 has
// zero domain-less events (verified against the analytics API on 2026-09-25,
// dealers: August 12.9M/10.5% empty, September 0). Months before the fix keep
// their gap forever — August and earlier are frozen — so warn whoever is
// reading them.
const TRACKER_FIX = { year: 2026, month: 8 };   // September, 0-indexed like Date
const beforeTrackerFix = (monthYear) => {
    const [year, month] = monthYear.split('-').map(Number);
    return year < TRACKER_FIX.year ||
        (year === TRACKER_FIX.year && month < TRACKER_FIX.month);
};

// First entry is the default product on page load.
const CATEGORIES = [
    { label: 'Dealers', value: 'dealers' },
    { label: 'Business AI', value: 'business_ai' },
    { label: 'Real Estate', value: 'realestates' },
    { label: 'Firstpage', value: 'firstpage' },
    { label: 'Survey', value: 'survey' }
];

// Cost allocation starts at January 2026 — the aggregator's backfill floor.
// Earlier months would silently fall back to the live analytics API, which has
// event counts but no dollars; keep this in step with the Lambda's BACKFILL_FLOOR.
const FLOOR = { year: 2026, month: 0 };   // month is 0-indexed, like Date

const getMonthYearOptions = () => {
    const options = [];
    const today = new Date();

    // Newest first, back to the floor.
    for (let d = new Date(today.getFullYear(), today.getMonth(), 1);
         d.getFullYear() > FLOOR.year ||
         (d.getFullYear() === FLOOR.year && d.getMonth() >= FLOOR.month);
         d.setMonth(d.getMonth() - 1)) {
        options.push({
            value: `${d.getFullYear()}-${d.getMonth()}`,
            label: d.toLocaleString('default', { month: 'long', year: 'numeric' })
        });
    }
    return options;
};

const monthYearLabel = (monthYear) => {
    const [year, month] = monthYear.split('-').map(Number);
    return new Date(year, month, 1)
        .toLocaleString('default', { month: 'long', year: 'numeric' });
};

const calculateDateRange = (monthYear) => {
    const [year, month] = monthYear.split('-').map(Number);
    const today = new Date();
    const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();
    
    const startDate = new Date(year, month, 1);
    let endDate;
    
    if (isCurrentMonth) {
        endDate = today;
    } else {
        endDate = new Date(year, month + 1, 0); // Last day of month
    }
    
    return {
        from: startDate.toISOString().split('T')[0],
        to: endDate.toISOString().split('T')[0]
    };
};

// Results keyed by cat|from|to, kept for the life of the tab. Flicking between
// the two dropdowns used to cost a full ~5s round trip each way, even when
// returning to a combination already fetched a second earlier. A closed month
// can never change, so re-fetching it is pure waste.
// Module scope, not component state: it survives unmounting the page.
const costsCache = new Map();
const CACHE_TTL_MS = 10 * 60 * 1000;   // matches the Lambda's open-period TTL

const readCache = (key) => {
    const hit = costsCache.get(key);
    if (!hit) return null;
    // A month the aggregator has pinned will never be recomputed — the TTL
    // only exists for data that can still move.
    if (!hit.data.meta?.frozen && Date.now() - hit.at > CACHE_TTL_MS) {
        costsCache.delete(key);
        return null;
    }
    return hit;
};

function MethodologyModal({ meta, category, onClose }) {
    // Escape to dismiss, and the page behind must not scroll while it is open.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    const label = CATEGORIES.find((c) => c.value === category)?.label || category;
    const money = (n) => `$${Number(n || 0).toFixed(2)}`;
    const num = (n) => Number(n || 0).toLocaleString();

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="method-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-head">
                    <h3 id="method-title" className="modal-title">How these numbers are calculated</h3>
                    <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    <p className="modal-lead">
                        There is no per-domain bill from AWS. These figures take the spend AWS
                        <em> can</em> attribute — by resource tag — and divide it across domains in
                        proportion to how much tracked activity each one generated.
                    </p>

                    <ol className="method-steps">
                        <li>
                            <strong>Tagged AWS spend — {money(meta.aws_direct_cost)}</strong>
                            <span>
                                Unblended cost from AWS Cost Explorer for every resource tagged
                                <code>Project</code> = <code>{label}</code>, over {meta.period_from} to {meta.period_to}.
                            </span>
                        </li>
                        <li>
                            <strong>Share of Elastic Tracking — {money(meta.tracking_share)}</strong>
                            <span>
                                The tracking platform is shared by every product, so its bill is split
                                by volume rather than charged to one of them:
                                <code>pool × this product's events ÷ all events</code>.
                            </span>
                        </li>
                        <li>
                            <strong>Per domain</strong>
                            <span>
                                Each domain takes its slice of the {money(meta.category_cost)} total in
                                proportion to events:
                                <code>total × domain events ÷ {num(meta.category_impressions)}</code>.
                            </span>
                        </li>
                    </ol>

                    <h4 className="method-heading">Worth knowing</h4>
                    <ul className="method-notes">
                        <li>
                            <strong>Recalculated once a day</strong>, not per visit — the analytics API
                            takes seconds per product. Last run {meta.last_updated
                                ? new Date(meta.last_updated).toLocaleString()
                                : 'unknown'}.
                        </li>
                        {meta.truncated && (
                            <li>
                                <strong>Only the top 1000 domains are itemised.</strong> The analytics API
                                caps grouped results there. The remaining {num(meta.remainder_impressions)} events
                                ({money(meta.remainder_cost)}) are counted in the total but have no row of
                                their own, so the rows plus that remainder reconcile exactly.
                            </li>
                        )}
                        <li>
                            <strong>Unattributed is real activity</strong>, not an error. Those events
                            consumed infrastructure, so they carry cost — they just cannot be billed to a
                            specific domain.
                        </li>
                        <li>
                            <strong>These are gross figures.</strong> AWS credits, savings plans and
                            reserved-instance discounts are not tagged to any product, so they are not
                            deducted here. Actual paid cost is somewhat lower.
                        </li>
                        <li>
                            <strong>Cost per event is not comparable across products</strong> — it
                            varies roughly fourfold — which is why each product is allocated from its own
                            pool rather than one global rate.
                        </li>
                    </ul>
                </div>

                <div className="modal-foot">
                    <button type="button" className="btn btn-neutral" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

const CostsList = () => {
    useNProgressLoader();

    const [costs, setCosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Defaults to the first option in CATEGORIES; keep the two in step.
    const [selectedCat, setSelectedCat] = useState(CATEGORIES[0].value);
    const [selectedMonthYear, setSelectedMonthYear] = useState(() => {
        const today = new Date();
        return `${today.getFullYear()}-${today.getMonth()}`;
    });
    // Bumping this re-runs the effect; combined with dropping the cache entry it
    // forces a genuine round trip.
    const [reloadToken, setReloadToken] = useState(0);
    const [fetchedAt, setFetchedAt] = useState(null);
    // Allocation metadata (category totals, truncation, aggregator run time).
    // Null when we fell back to the live upstream, which has no cost data.
    const [meta, setMeta] = useState(null);
    const [showMethod, setShowMethod] = useState(false);

    const refreshNow = () => {
        const range = calculateDateRange(selectedMonthYear);
        costsCache.delete(`${selectedCat}|${range.from}|${range.to}`);
        setReloadToken((n) => n + 1);
    };

    useEffect(() => {
        let cancelled = false;

        async function fetchCosts() {
            const dateRange = calculateDateRange(selectedMonthYear);
            const cacheKey = `${selectedCat}|${dateRange.from}|${dateRange.to}`;

            const cached = readCache(cacheKey);
            if (cached) {
                setCosts(cached.data.rows);
                setMeta(cached.data.meta);
                setFetchedAt(cached.at);
                setError('');
                setLoading(false);
                return;
            }

            try {
                // Changing either dropdown refires this; without the flag the stale
                // list stayed on screen with no indication anything was happening.
                setLoading(true);
                window.dispatchEvent(new Event('apiRequest'));
                // A closed month is served with max-age=86400, so after the
                // aggregator backfills one, the browser keeps answering with
                // its stale copy for up to a day and "Check for new data"
                // silently no-ops. A throwaway param makes the URL new, which
                // is the only cache-bypass a plain GET gets.
                const bust = reloadToken ? `&r=${Date.now()}` : '';
                const restOperation = get({
                    apiName: 'costs',
                    path: `/costs?cat=${selectedCat}&from=${dateRange.from}&to=${dateRange.to}${bust}`
                });
                const { body } = await restOperation.response;
                const jsonResponse = await body.json();
                
                // Two shapes. A bare array is the live upstream (event counts only);
                // an object is the daily allocation, which also carries dollars.
                let rows, allocation = null;
                if (Array.isArray(jsonResponse)) {
                    rows = jsonResponse;
                } else if (jsonResponse && Array.isArray(jsonResponse.domains)) {
                    rows = jsonResponse.domains;
                    const { domains, ...rest } = jsonResponse;
                    allocation = rest;
                } else {
                    throw new Error('Invalid data format received');
                }

                const sortedCosts = rows.sort((a, b) => b.total - a.total);
                costsCache.set(cacheKey, { data: { rows: sortedCosts, meta: allocation }, at: Date.now() });
                window.dispatchEvent(new Event('apiResponse'));
                // Rapid dropdown changes can land out of order; ignore a response
                // whose request is no longer the current one.
                if (cancelled) return;
                setCosts(sortedCosts);
                setMeta(allocation);
                setFetchedAt(Date.now());
                setError('');
                setLoading(false);
            } catch (e) {
                // Detailed error logging
                console.group('Costs API Error');
                console.error('Error details:', {
                    message: e.message,
                    name: e.name,
                    response: e.response,
                    stack: e.stack
                });
                console.groupEnd();

                window.dispatchEvent(new Event('apiResponse'));
                if (cancelled) return;

                // User-friendly error message
                if (e instanceof ApiError) {
                    setError('Unable to load costs data. Please try again later.');
                } else {
                    setError('Something went wrong while fetching the data. Please try again.');
                }
                setLoading(false);
            }
        }

        fetchCosts();
        return () => { cancelled = true; };
    }, [selectedCat, selectedMonthYear, reloadToken]);

    // Guard the empty case: Math.max(...[]) is -Infinity.
    const maxTotal = costs.length ? Math.max(...costs.map(item => item.total)) : 0;

    const controls = (
        <div className="costs-header">
            <div>
                <div className="title-row">
                    <h2 className="page-title">Costs Analysis</h2>
                    <span className="powered-by">Powered by Elastic Tracking</span>
                </div>
                <p className="page-subtitle">
                    {meta
                        ? 'AWS spend for this product, shared across domains in proportion to tracked activity.'
                        : 'See which domains drove the most tracked activity, busiest first.'}
                </p>
                {fetchedAt && (
                    // Kept on its own line — bolting the timestamp onto the end of the
                    // description made both harder to read.
                    <p className="page-meta">
                        {meta?.frozen ? (
                            // Pinned by the aggregator once the month settled; offering
                            // "Check for new data" here would promise a change that can
                            // never come.
                            <span
                                className="frozen-badge"
                                title={`Pinned ${meta.frozen_at
                                    ? new Date(meta.frozen_at).toLocaleDateString()
                                    : ''} — this month is closed and its figures will not change.`}
                            >
                                Final figures
                            </span>
                        ) : (
                            <>
                                Last updated at {new Date(fetchedAt).toLocaleTimeString([], {
                                    hour: 'numeric', minute: '2-digit'
                                })}
                                <span className="page-meta-sep">·</span>
                                <button type="button" className="auth-link inline-link" onClick={refreshNow}>
                                    Check for new data
                                </button>
                            </>
                        )}
                    </p>
                )}
            </div>
            <div className="selectors-container">
                {/* These selects had no label of any kind — a screen reader announced
                    "combo box, August 2026" with no clue what it filtered. */}
                <div className="select-field">
                    <label className="select-label" htmlFor="cost-month">Month</label>
                    <select
                        id="cost-month"
                        value={selectedMonthYear}
                        onChange={(e) => setSelectedMonthYear(e.target.value)}
                        className="month-select"
                    >
                        {getMonthYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="select-field">
                    <label className="select-label" htmlFor="cost-cat">Product</label>
                    <select
                        id="cost-cat"
                        value={selectedCat}
                        onChange={(e) => setSelectedCat(e.target.value)}
                        className="category-select"
                    >
                        {CATEGORIES.map(category => (
                            <option key={category.value} value={category.value}>
                                {category.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    return (
        <div className="costs-container">
            {controls}

            {!loading && !error && beforeTrackerFix(selectedMonthYear) && (
                <p className="history-note" role="note">
                    <strong>Reading an older month:</strong> through August 2026 the
                    tracker could not record a page domain for banner traffic served
                    inside srcdoc iframes. That activity shows as Unattributed, and
                    every real domain is undercounted by its share. The tracking tag
                    was fixed in September 2026, so compare months across that
                    boundary with care.
                </p>
            )}

            {loading && <p className="loading-state" role="status">Loading costs…</p>}

            {!loading && error && <p className="error-state" role="alert">{error}</p>}

            {!loading && !error && !costs.length && (
                <p className="empty-state">No cost data for this product and month.</p>
            )}

            {!loading && !error && meta && (
                <div className="alloc-summary">
                    <span className="alloc-figure">
                        <span className="alloc-value">{formatMoney(meta.category_cost)}</span>
                        <span className="alloc-label">total for {monthYearLabel(selectedMonthYear)}</span>
                    </span>
                    <span className="alloc-breakdown">
                        {formatMoney(meta.aws_direct_cost)} tagged AWS spend
                        <span className="alloc-plus">+</span>
                        {formatMoney(meta.tracking_share)} share of Elastic Tracking
                    </span>
                    {meta.truncated && (
                        // Only price the remainder when there is one. With a
                        // zero remainder, "· $0.00 not itemised" claimed a
                        // precision the 1000-row cap cannot actually provide.
                        <span
                            className="alloc-note"
                            title={meta.remainder_cost >= 0.005
                                ? `The analytics API returns at most 1000 domains. The remaining ${meta.remainder_impressions.toLocaleString()} events (${formatMoney(meta.remainder_cost)}) are real but not itemised below.`
                                : 'The analytics API returns at most 1000 domains; anything beyond the cap is not itemised below.'}
                        >
                            top 1000 domains{meta.remainder_cost >= 0.005 &&
                                ` · ${formatMoney(meta.remainder_cost)} not itemised`}
                        </span>
                    )}
                    <button
                        type="button"
                        className="alloc-help"
                        onClick={() => setShowMethod(true)}
                        aria-label="How these numbers are calculated"
                        title="How these numbers are calculated"
                    >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.7.25-1.2.9-1.2 1.6v.5" />
                            <path d="M12 17h.01" />
                        </svg>
                    </button>
                </div>
            )}

            {showMethod && meta && (
                <MethodologyModal meta={meta} category={selectedCat} onClose={() => setShowMethod(false)} />
            )}

            {!loading && !error && costs.length > 0 && (
                <div className="costs-list">
                    {costs.map((item, index) => {
                        // Events the tracker could not attribute to a domain. Flagged
                        // rather than hidden: it is ~20% of the total, and treating it
                        // as just another domain makes it look like a real property.
                        const unattributed = !item.domain;
                        return (
                            <div
                                key={index}
                                className={`cost-item${unattributed ? ' cost-item--unattributed' : ''}`}
                            >
                                <div className="cost-info">
                                    {unattributed ? (
                                        <span
                                            className="domain domain--unattributed"
                                            data-tooltip={UNATTRIBUTED_HELP}
                                            tabIndex={0}
                                        >
                                            Unattributed
                                            <span className="domain-flag" aria-hidden="true">?</span>
                                            <span className="visually-hidden">{UNATTRIBUTED_HELP}</span>
                                        </span>
                                    ) : (
                                        <span className="domain">{item.domain}</span>
                                    )}
                                    <span className="total">
                                        {typeof item.cost === 'number' && (
                                            <span className="total-money">{formatMoney(item.cost)}</span>
                                        )}
                                        <span className="total-events">
                                            {formatNumber(item.total)} events
                                        </span>
                                    </span>
                                </div>
                                <div className="bar-container">
                                    <div
                                        className="bar"
                                        style={{ width: `${(item.total / maxTotal) * 100}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CostsList;